import { useT } from '../lib/i18n';
import { ResultCard } from '../components/ResultCard';
import type { ResultsRenderer, ResultsView } from './results';

/**
 * The default renderer: our own tiles.
 *
 * This is the host that has no markup of its own to work with — the standalone
 * app, and a WordPress page with nowhere to put results. It is one of two
 * interchangeable implementations of `ResultsRenderer` and holds no search
 * logic at all; everything it draws comes in through `ResultsView`.
 *
 * That it is this short is the point. All the behaviour a park's own renderer
 * would otherwise have to reimplement — which accommodations match, what each
 * costs, whether it can be booked directly — happened before this function was
 * called.
 */
export const tileResults: ResultsRenderer = (view: ResultsView) => <Tiles view={view} />;

function Tiles({ view }: { view: ResultsView }) {
  const t = useT();
  const { results, arrival, nights } = view;

  if (arrival === null || nights === null) {
    return <p className="pw-empty">{t('results.chooseDate')}</p>;
  }
  if (results.length === 0) {
    return <p className="pw-empty">{t('results.none')}</p>;
  }

  return (
    <div className="pw-results">
      {results.map((r) => (
        <ResultCard
          key={r.accommodation.id}
          accommodation={r.accommodation}
          defs={view.attributes}
          quote={r.quote}
          loading={r.loading}
          arrival={arrival}
          nights={nights}
          free={r.free}
          // Decided once, in useSearch, which is the only place that knows
          // whether this deployment counts units at all.
          available={r.available}
          booking={r.booking}
          currency={view.currency}
          chosen={view.chosen === r.accommodation.id}
          onChoose={() => view.choose(r.accommodation.id)}
        />
      ))}
    </div>
  );
}
